import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import assessedIcon from "../assets/assessed-icon.svg";
import assetsIcon from "../assets/assets-icon.svg";
import pendingIcon from "../assets/pending-icon.svg";
import ratingIcon from "../assets/ratings-icon.svg";
import { useDashboardStore } from "../stores/dashboardStore";
import MapComponent from "../components/MapComponent";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { dashboardData, isLoading, error, fetchDashboardData } = useDashboardStore();
  const [isUpdating, setIsUpdating] = React.useState(false);
  const updateDashboardData = useDashboardStore(
    (state) => state.updateDashboardData
  );
  const [selectedMonth, setSelectedMonth] = React.useState("January"); // Default month
  React.useEffect(() => {
    const updateData = async () => {
      setIsUpdating(true);
      try {
        await updateDashboardData();
      } finally {
        setIsUpdating(false);
      }
    };

    updateData();

    const intervalId = setInterval(() => {
      updateData();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [updateDashboardData]);
  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    fetchDashboardData(newMonth.toLowerCase()); // Immediately fetch data for the new month
  };

  const [selectedYear, setSelectedYear] = React.useState("2025"); // Default year
  // Fetch data when the component mounts or month changes
  React.useEffect(() => {
    fetchDashboardData(selectedMonth.toLowerCase(), selectedYear); // Convert to lowercase to match backend logic
  }, [selectedMonth, selectedYear, fetchDashboardData]);


  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
    fetchDashboardData(selectedMonth.toLowerCase(), newYear); // Pass year to fetch
  };

  // Move all hooks to the top before any conditional returns
  const data = React.useMemo(
    () => [
      {
        id: 1,
        counts: dashboardData.roadAssessed?.totalRoadsAssessed || 0,
        icon: (
          <img src={assessedIcon} className="p-2 rounded-lg bg-[#dbeaff]" />
        ),
        name: "Total Roads Assessed",
      },
      {
        id: 2,
        counts: dashboardData.assetsTagged?.assetsTagged || 0,
        icon: <img src={assetsIcon} className="p-2 rounded-lg bg-[#ffedd5]" />,
        name: "Numbers Of Assets Tagged",
      },
      {
        id: 3,
        counts: dashboardData.pendingMaintenance?.pendingMaintenance || 0,
        icon: <img src={pendingIcon} className="p-2 rounded-lg bg-[#fee2e1]" />,
        name: "Pending Maintenance Requests",
      },
      {
        id: 4,
        counts:
          dashboardData.overallConditionRating?.overallConditionRating || 0,
        icon: <img src={ratingIcon} className="p-2 rounded-lg bg-[#cffbfe]" />,
        name: "Overall Road Condition Ratings",
      },
    ],
    [dashboardData]
  );

  // Bar chart data
  const barChartData = React.useMemo(
    () => ({
      labels: ["Critical", "High", "Medium", "Low", "Normal"],
      datasets: [
        {
          label: "Road Conditions",
          data: [
            dashboardData.roadConditionAssessment?.critical || 0,
            dashboardData.roadConditionAssessment?.high || 0,
            dashboardData.roadConditionAssessment?.medium || 0,
            dashboardData.roadConditionAssessment?.low || 0,
            dashboardData.roadConditionAssessment?.normal || 0,
          ],
          backgroundColor: [
            "#ff7d74",
            "#ee9c5e",
            "#fbd24e",
            "#b7db40",
            "#19b246",
          ],
          barThickness: 20,
        },
      ],
    }),
    [dashboardData.roadConditionAssessment]
  );

  // Bar chart options
  const barChartOptions = React.useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }),
    []
  );

  // Line chart data
  const lineChartData = React.useMemo(() => {
    const monthlyData = dashboardData.averageConditionPerMonth?.data || [];
    const dataMap = monthlyData.reduce(
      (acc, item) => {
        if (item.month && item.averageRating !== undefined) {
          acc[item.month] = item.averageRating;
        }
        return acc;
      },
      {
        Jan: 0,
        Feb: 0,
        Mar: 0,
        Apr: 0,
        May: 0,
        Jun: 0,
        Jul: 0,
        Aug: 0,
        Sep: 0,
        Oct: 0,
        Nov: 0,
        Dec: 0,
      }
    );

    return {
      labels: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      datasets: [
        {
          label: "Average Condition Rating",
          data: [
            dataMap.Jan,
            dataMap.Feb,
            dataMap.Mar,
            dataMap.Apr,
            dataMap.May,
            dataMap.Jun,
            dataMap.Jul,
            dataMap.Aug,
            dataMap.Sep,
            dataMap.Oct,
            dataMap.Nov,
            dataMap.Dec,
          ],
          borderColor: "#3b82f6",
          tension: 0.4,
          fill: false,
          pointRadius: 5, // Highlight data points
          pointHoverRadius: 7,
        },
      ],
    };
  }, [dashboardData.averageConditionPerMonth]);

  // Line chart options
  const lineChartOptions = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => `Average Condition Rating: ${context.raw}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          min: 0,
          max: 10,
          ticks: {
            stepSize: 1,
          },
        },
      },
    }),
    []
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-0">
      {isUpdating && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
            <p className="mt-4 text-lg">Updating dashboard data...</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 grid-rows-1 gap-4 p-4 mt-20">
        <div className="grid grid-cols-2 gap-2">
          {data.map((item) => (
            <div
              key={item.id}
              className="bg-white p-4 shadow-md rounded-md  m-4"
            >
              <div className="flex text-xl justify-between items-center gap-2">
                <h1 className="text-3xl font-bold">{item.counts}</h1>
                <span>{item.icon}</span>
              </div>
              <p className="text-lg mt-12">{item.name}</p>
            </div>
          ))}
        </div>

        <div className="bg-white p-4 shadow-md rounded-md w-full m-4">
          <div className="flex justify-between mb-4 ">
            <h1 className="font-medium text-lg">Road Condition Assessment</h1>
            <select
              name="months"
              id="months"
              value={selectedMonth}
              onChange={handleMonthChange}
              className="border p-2 rounded-md"
            >
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>
          <div className="h-64">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>

        <div className="bg-white p-4 shadow-md rounded-md w-full m-4 relative z-0">
          <h1 className="font-medium text-lg mb-2">
            Interactive Map Road Segments
          </h1>
          <MapComponent isDashboard={true} mapData={dashboardData || []} />
        </div>
        <div className="bg-white p-4 shadow-md rounded-md w-full m-4">
          <div className="flex justify-between mb-4">
            <h1 className="font-medium text-lg">
              Average Condition Rating Per Month
            </h1>
            <select
            name="years"
            id="years"
            value={selectedYear}
            onChange={handleYearChange}
            className="border p-2 rounded-md"
           >
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
            <option value="2028">2028</option>
            <option value="2029">2029</option>
            <option value="2030">2030</option>
           {/* Add more years as needed */}
          </select>
        </div>
        <div className="bg-white p-4 shadow-md rounded-md w-full m-4">
        <div className="h-96">
          <Line data={lineChartData} options={lineChartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
